import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, X, File, CheckCircle, AlertCircle, Eye, 
  Clock, Zap, AlertTriangle, Lightbulb, Settings,
  Play, Pause, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAdvancedFileUpload } from '@/hooks/useAdvancedFileUpload';

interface EnhancedFileUploadProps {
  onFileUploaded?: (filePath: string, fileName: string) => void;
  onFilesProcessed?: (results: any[]) => void;
}

export const EnhancedFileUpload = ({ 
  onFileUploaded,
  onFilesProcessed
}: EnhancedFileUploadProps) => {
  const { 
    uploads, 
    processFile, 
    processBatch, 
    removeUpload, 
    clearCompleted,
    retryUpload,
    approvePreview,
    batchMode,
    setBatchMode,
    autoProcess,
    setAutoProcess
  } = useAdvancedFileUpload();
  
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedTab, setSelectedTab] = useState('upload');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      if (acceptedFiles.length > 1 && !batchMode) {
        setBatchMode(true);
      }

      if (batchMode && acceptedFiles.length > 1) {
        await processBatch(acceptedFiles);
      } else {
        for (const file of acceptedFiles) {
          const filePath = await processFile(file);
          if (filePath && onFileUploaded) {
            onFileUploaded(filePath, file.name);
          }
        }
      }

      if (onFilesProcessed) {
        onFilesProcessed(uploads);
      }
    } catch (error) {
      console.error('File upload error:', error);
    }
  }, [processFile, processBatch, onFileUploaded, onFilesProcessed, uploads, batchMode, setBatchMode]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false)
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'validating':
        return <Clock className="h-4 w-4 text-primary animate-pulse" />;
      case 'processing':
        return <Zap className="h-4 w-4 text-primary animate-pulse" />;
      case 'preview':
        return <Eye className="h-4 w-4 text-warning" />;
      default:
        return <File className="h-4 w-4 text-primary" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'destructive';
      case 'preview':
        return 'warning';
      case 'processing':
      case 'validating':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const renderUploadSummary = () => {
    const total = uploads.length;
    const completed = uploads.filter(u => u.status === 'completed').length;
    const errors = uploads.filter(u => u.status === 'error').length;
    const processing = uploads.filter(u => ['validating', 'uploading', 'processing'].includes(u.status)).length;
    const previews = uploads.filter(u => u.status === 'preview').length;

    if (total === 0) return null;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">{total}</div>
          <div className="text-sm text-muted-foreground">Total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-success">{completed}</div>
          <div className="text-sm text-muted-foreground">Completed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-warning">{processing}</div>
          <div className="text-sm text-muted-foreground">Processing</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-destructive">{errors}</div>
          <div className="text-sm text-muted-foreground">Errors</div>
        </div>
      </div>
    );
  };

  const renderFileItem = (upload: any) => (
    <Card key={upload.id} className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            {getStatusIcon(upload.status)}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* File header */}
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium truncate">{upload.file.name}</h4>
              <div className="flex items-center space-x-2">
                <Badge variant={getStatusColor(upload.status) as any}>
                  {upload.status}
                </Badge>
                {upload.status === 'error' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => retryUpload(upload.id)}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                {upload.status === 'preview' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => approvePreview(upload.id)}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUpload(upload.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* File metadata */}
            <div className="text-xs text-muted-foreground mb-2">
              {(upload.file.size / 1024 / 1024).toFixed(1)} MB • 
              {upload.metadata.detectedSupplier && ` ${upload.metadata.detectedSupplier} • `}
              {upload.metadata.detectedServiceType && ` ${upload.metadata.detectedServiceType} • `}
              Est. {(upload.metadata.estimatedProcessingTime / 1000).toFixed(1)}s
            </div>

            {/* Progress bar */}
            {['validating', 'uploading', 'processing'].includes(upload.status) && (
              <Progress value={upload.progress} className="mb-2" />
            )}

            {/* Validation feedback */}
            {upload.validation.errors.length > 0 && (
              <Alert className="mb-2">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {upload.validation.errors.join(', ')}
                </AlertDescription>
              </Alert>
            )}

            {upload.validation.warnings.length > 0 && (
              <Alert className="mb-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {upload.validation.warnings.join(', ')}
                </AlertDescription>
              </Alert>
            )}

            {upload.validation.suggestions.length > 0 && (
              <Alert className="mb-2">
                <Lightbulb className="h-4 w-4" />
                <AlertDescription>
                  {upload.validation.suggestions.join(', ')}
                </AlertDescription>
              </Alert>
            )}

            {/* Preview data */}
            {upload.status === 'preview' && upload.previewData && (
              <div className="bg-muted p-3 rounded-lg mb-2">
                <h5 className="font-medium mb-2">Detected Information:</h5>
                <div className="space-y-1 text-sm">
                  {upload.previewData.supplierName && (
                    <div>Supplier: <span className="font-medium">{upload.previewData.supplierName}</span></div>
                  )}
                  {upload.previewData.serviceType && (
                    <div>Service: <span className="font-medium">{upload.previewData.serviceType}</span></div>
                  )}
                  {upload.previewData.estimatedAmount && (
                    <div>Amount: <span className="font-medium">${upload.previewData.estimatedAmount.toFixed(2)}</span></div>
                  )}
                  {upload.previewData.billDate && (
                    <div>Date: <span className="font-medium">{upload.previewData.billDate}</span></div>
                  )}
                  <div>Confidence: <span className="font-medium">{(upload.previewData.confidence * 100).toFixed(0)}%</span></div>
                </div>
              </div>
            )}

            {/* Error message */}
            {upload.error && (
              <p className="text-xs text-destructive mt-1">{upload.error}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="progress">Progress ({uploads.length})</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          {/* Upload Area */}
          <Card className={`border-2 border-dashed transition-all duration-200 ${
            isDragActive 
              ? 'border-primary bg-primary/5 scale-105' 
              : 'border-muted-foreground/25 hover:border-primary/50'
          }`}>
            <CardContent className="p-8">
              <div
                {...getRootProps()}
                className="flex flex-col items-center justify-center space-y-4 cursor-pointer"
              >
                <input {...getInputProps()} />
                <div className={`p-6 rounded-full transition-all duration-200 ${
                  isDragActive 
                    ? 'bg-primary text-primary-foreground scale-110' 
                    : 'bg-muted hover:bg-muted/80'
                }`}>
                  <Upload className="h-12 w-12" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold mb-2">
                    {isDragActive ? 'Drop your files here' : 'Upload your utility bills'}
                  </p>
                  <p className="text-muted-foreground">
                    Drop files here or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Supports PDF, JPEG, PNG, WebP • Max 50MB per file
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Advanced validation • Smart detection • Batch processing
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick stats */}
          {renderUploadSummary()}
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          {uploads.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <File className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No files uploaded yet</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {renderUploadSummary()}
              
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">File Processing Status</h3>
                <div className="flex space-x-2">
                  {uploads.some(u => u.status === 'completed') && (
                    <Button variant="outline" size="sm" onClick={clearCompleted}>
                      Clear Completed
                    </Button>
                  )}
                  {batchMode && (
                    <Badge variant="secondary">Batch Mode</Badge>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {uploads.map(renderFileItem)}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Upload Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Auto-process files</h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically process files after upload without preview
                  </p>
                </div>
                <Switch
                  checked={autoProcess}
                  onCheckedChange={setAutoProcess}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Batch mode</h4>
                  <p className="text-sm text-muted-foreground">
                    Process multiple files simultaneously
                  </p>
                </div>
                <Switch
                  checked={batchMode}
                  onCheckedChange={setBatchMode}
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Smart Features</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>✓ Advanced file validation</div>
                  <div>✓ Supplier auto-detection</div>
                  <div>✓ Processing time estimation</div>
                  <div>✓ Data preview before processing</div>
                  <div>✓ Smart suggestions</div>
                  <div>✓ Error recovery</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};